import React, { FC, useCallback, useEffect, useState } from 'react';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../webux/webux-layouts/WebUxPage';
import { Button, Modal, Space } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { QueryBuilder } from '../../components/molecules/QueryBuilder/QueryBuilder';
import { DataTable, DataTablePagination } from '../../components/molecules/DataTable/DataTable';
import { useConnection } from '../../connection/Application';
import { InfoView, InfoViewItem } from '../../components/info-view/InfoView';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { FlexBox } from '../../components/atoms/FlexBox';
import { FormModal, useFormModal } from '../../components/form-modal/FormModal';
import { DesignationData } from '../../entity/Designation';
import Form from '../../form/Form';

const columns = [
  {
    title: 'Id',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Designation',
    dataIndex: 'value',
    width: '60%',
    key: 'value',
  },
];

const schema = [
  {
    id: 'value',
    name: 'Designation',
    type: 'string',
  },
];

const Designation: FC = () => {
  const [visible, setVisible] = useState<boolean>(false);
  const [filter, setFilter] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState();
  const [total, setTotal] = useState<number>(0);
  const [current, setCurrent] = useState<{ id: number; value: string }>();
  const [pagination, setPagination] = useState<DataTablePagination>({ page: 1, size: 10 });
  const connection = useConnection();
  const createForm = useFormModal<DesignationData>();
  const editForm = useFormModal<DesignationData>();

  const getDesignation = useCallback(
    (filter: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('admin/designation', { ...pagination, ...filter })
        .then((response) => {
          setData(response.designation.map((e: any) => ({ ...e, key: e.id })));
          setTotal(response.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );

  useEffect(() => {
    getDesignation({}, { page: 1, size: 10 });
  }, [connection, getDesignation]);

  const addDesignation = useCallback(
    async (designation: DesignationData) => {
      try {
        await connection.post('admin/designation', designation);
        createForm.close();
        getDesignation(filter, pagination);
      } catch (e: any) {
        if (e.body?.body?.errors) {
          createForm.setErrors(e.body.body.errors);
        } else {
          createForm.setErrors([]);
        }
      }
    },
    [connection, createForm, filter, getDesignation, pagination],
  );

  const editDesignation = useCallback(
    async (designation: DesignationData) => {
      try {
        const designationId = current && current.id;
        designation.id = designationId;
        await connection.patch(`admin/designation/${designationId}`, designation);
        editForm.close();
        setVisible(false);
        getDesignation(filter, pagination);
      } catch (e: any) {
        if (e.body?.body?.errors) {
          editForm.setErrors(e.body.body.errors);
        } else {
          editForm.setErrors([]);
        }
      }
    },
    [connection, current, editForm, filter, getDesignation, pagination],
  );

  const search = useCallback(
    (filter: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('admin/designation', { ...pagination, ...filter })
        .then((response) => {
          setData(response.designation);
          setTotal(response.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );

  const deleteDesignation = useCallback(() => {
    const designationId = current && current.id;
    Modal.confirm({
      title: 'Delete Designation',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure delete to this Designation?',
      okText: 'Delete',
      okType: 'danger',
      onOk() {
        connection.delete(`admin/designation/${designationId}`).then(() => {
          setVisible(false);
          getDesignation(filter, pagination);
        });
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  }, [connection, current, filter, getDesignation, pagination]);

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Designation"
        subTitle="Designation management"
      />
      <WebUxPageContent>
        <WebUxPageItem
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <QueryBuilder
            searchPlaceholder="Search Designation"
            search={true}
            value={filter}
            onChange={setFilter}
            onSearch={(value) => {
              setPagination({ page: 1, size: 10 });
              search(value, pagination);
            }}
            schema={[]}
          />
          <Space>
            <Button
              onClick={() => {
                setFilter({});
                setPagination({ page: 1, size: 10 });
                search({}, { page: 1, size: 10 });
              }}>
              Reset
            </Button>
            <Button type="primary">Search</Button>
          </Space>
        </WebUxPageItem>
        <WebUxPageItem>
          <Space direction={'vertical'} style={{ width: '100%' }} size={16}>
            <FlexBox horizontalAlign={'right'}>
              <Button
                icon={<PlusOutlined />}
                key="1"
                type="primary"
                onClick={() => createForm.open()}>
                New Designation
              </Button>
            </FlexBox>
            <DataTable
              columns={columns}
              total={total}
              loading={loading}
              dataSource={data}
              onPaginationChange={(p) => {
                setPagination(p);
                if (pagination.page !== p.page || pagination.size !== p.size) {
                  search(filter, p);
                }
              }}
              onClick={(item: { id: number; value: string }) => {
                setVisible(true);
                setCurrent(item);
              }}
              dtPagination={pagination}
            />
          </Space>
        </WebUxPageItem>
      </WebUxPageContent>
      <Modal
        open={visible}
        title={'Designation'}
        onCancel={() => {
          setVisible(false);
        }}
        footer={
          <Space align={'end'}>
            <Button
              onClick={() => {
                setVisible(false);
              }}>
              Close
            </Button>
            <Button onClick={() => editForm.open(current)}>Edit</Button>
            <Button type="primary" onClick={deleteDesignation}>
              Delete
            </Button>
          </Space>
        }>
        <InfoView>
          <InfoViewItem title={(current && current.value) ?? ''} />
        </InfoView>
      </Modal>
      <FormModal title={'Add Designation'} onSubmit={addDesignation} form={createForm}>
        <Form.Items schema={schema} />
      </FormModal>
      <FormModal title={'Edit Designation'} onSubmit={editDesignation} form={editForm}>
        <Form.Items schema={schema} />
      </FormModal>
    </WebUxPage>
  );
};
export default Designation;
